import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { cn } from 'lib/helpers/utility';
import usePortalNavigate from 'lib/hooks/usePortalNavigate';
import { useTableContext } from 'lib/context/TableContext/TableContext';

import Button from 'components/shared/Button/Button';
import Typography from 'components/shared/Typography/Typography';
import { useRequestCommandBar } from 'components/Search/CommandBar/lib/commandBarState';

import { isTableFiltered, recordNameOf, showHighlighted } from '../lib/highlightFilter';

const missingText = (highlight, { isFiltered, hasMorePages }) => {
  if (highlight.id == null) return `No row matches “${highlight.match}”`;
  const name = recordNameOf(highlight);
  if (isFiltered) return `${name} isn't shown with the current filters`;
  return hasMorePages ? `${name} is on another page` : `${name} isn't in this list`;
};

const BannerText = ({ children, ...rest }) => (
  <Typography
    variant="span"
    size="xs"
    color="neutral-700"
    className="block truncate"
    parentClassName="min-w-0"
    {...rest}>
    {children}
  </Typography>
);

const lastSegmentOf = (pathname) => pathname.split('/').filter(Boolean).pop();

const isOverRecord = (highlight, pathname) =>
  highlight.id != null && lastSegmentOf(pathname) === String(highlight.id);

const HighlightBanner = ({
  highlight,
  isMissing = false,
  hasMorePages = false,
  isFloating: isPinned = false,
  onBack
}) => {
  const location = useLocation();
  const navigate = usePortalNavigate();
  const requestCommandBar = useRequestCommandBar();
  const table = useTableContext();
  const isRecord = highlight.id != null;
  const isBare = !isRecord && !highlight.match;
  const isFloating = isPinned || isOverRecord(highlight, location.pathname);

  const backToResults = () => {
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    requestCommandBar(highlight.back);
    onBack?.();
  };

  const banner = (
    <div
      data-qa="row-highlight-banner"
      className={cn(
        'flex min-w-0 items-center gap-3 border-solid border-primary-100 bg-primary-25 px-4',
        isFloating
          ? 'fixed left-1/2 top-0 z-[101] max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-b-lg border border-t-0 py-0.5 shadow-md'
          : 'border-0 border-b py-1'
      )}>
      <Button variant="link" size="xs" text="Back to results" onClick={backToResults} />
      {isMissing && !isBare && (
        <>
          <BannerText data-qa="row-highlight-missing">
            {missingText(highlight, { isFiltered: isTableFiltered(table), hasMorePages })}
          </BannerText>
          {(table?.changeFilters || table?.resetFilters) && (
            <Button
              variant="link"
              size="xs"
              text="Show it"
              onClick={() => showHighlighted(highlight, table)}
              data-qa="row-highlight-show"
            />
          )}
        </>
      )}
      {!isMissing && !isBare && (
        <BannerText>Showing {isRecord ? recordNameOf(highlight) : highlight.match}</BannerText>
      )}
    </div>
  );

  return isFloating ? createPortal(banner, document.body) : banner;
};

export default HighlightBanner;
